'use client';

// First: pdf.js needs Promise.withResolvers, which older Safari/Chrome lack.
import '@/lib/polyfills/promise-with-resolvers';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Book, Play, Pause } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PDFBookViewerProps {
  pdfUrl: string;
  bookTitle?: string;
  className?: string;
  enableAutoFlip?: boolean;
  autoFlipInterval?: number; // in milliseconds
  maxWidth?: number;
  showControls?: boolean;
  childPhotoUrl?: string; // Add photo URL for transformation display
  childName?: string; // Add child name for transformation display
  onLoadFailed?: () => void; // Lets the parent hide the viewer instead of showing an error box
}

export default function PDFBookViewer({ 
  pdfUrl, 
  bookTitle = "Book Preview",
  className = '',
  enableAutoFlip = false,
  autoFlipInterval = 5000,
  maxWidth = 700,
  showControls = true,
  childPhotoUrl,
  childName,
  onLoadFailed
}: PDFBookViewerProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isAutoFlipping, setIsAutoFlipping] = useState(enableAutoFlip);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pageWidth, setPageWidth] = useState(500);
  const autoFlipRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Check if mobile and set optimal page width for maximum usage
    const checkMobileAndSetWidth = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      if (mobile) {
        // Maximize mobile width - use nearly all available space
        const availableWidth = window.innerWidth - 16; // Minimal padding
        setPageWidth(availableWidth); // No cap - use full width
      } else {
        // Moderate desktop size for readability
        const containerWidth = Math.min(window.innerWidth * 0.5, maxWidth);
        const optimalWidth = Math.min(Math.max(500, containerWidth * 0.8), 600);
        setPageWidth(optimalWidth);
      }
    };
    
    checkMobileAndSetWidth();
    window.addEventListener('resize', checkMobileAndSetWidth);
    
    return () => window.removeEventListener('resize', checkMobileAndSetWidth);
  }, [maxWidth]);

  // Auto-flip functionality
  useEffect(() => {
    if (isAutoFlipping && numPages > 0) {
      autoFlipRef.current = setInterval(() => {
        setCurrentPage(prev => {
          const next = prev + 1;
          if (next > numPages) {
            return 1; // Reset to first page
          }
          return next;
        });
      }, autoFlipInterval);
    } else {
      if (autoFlipRef.current) {
        clearInterval(autoFlipRef.current);
        autoFlipRef.current = null;
      }
    }

    return () => {
      if (autoFlipRef.current) {
        clearInterval(autoFlipRef.current);
      }
    };
  }, [isAutoFlipping, numPages, autoFlipInterval]);

  // Trigger flip animation on page change
  useEffect(() => {
    setIsFlipping(true);
    const timer = setTimeout(() => setIsFlipping(false), 600);
    return () => clearTimeout(timer);
  }, [currentPage]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard events when not typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (currentPage > 1) {
            handlePageChange('prev');
          }
          break;
        case 'ArrowRight':
        case ' ': // Spacebar
          e.preventDefault();
          if (currentPage < numPages) {
            handlePageChange('next');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, numPages]);

  // Touch/swipe support for mobile
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartX || !touchStartY) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const diffX = touchStartX - touchEndX;
      const diffY = touchStartY - touchEndY;

      // Only handle horizontal swipes (ignore vertical scrolling)
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0 && currentPage < numPages) {
          handlePageChange('next');
        } else if (diffX < 0 && currentPage > 1) {
          handlePageChange('prev');
        }
      }

      touchStartX = 0;
      touchStartY = 0;
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentPage, numPages]);

  const handlePageChange = (direction: 'prev' | 'next') => {
    // Prevent rapid clicking that can crash the PDF worker
    const now = Date.now();
    const lastCall = (handlePageChange as any).lastCall;
    if (lastCall && now - lastCall < 500) {
      return;
    }
    (handlePageChange as any).lastCall = now;
    
    setCurrentPage(prev => {
      if (direction === 'prev') {
        return Math.max(1, prev - 1);
      } else {
        return Math.min(numPages, prev + 1);
      }
    });
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    setPdfError('Failed to load PDF');
    onLoadFailed?.();
    // Reset auto-flipping on error to prevent further issues
    setIsAutoFlipping(false);
  };

  return (
    <div className={`${className}`}>
      {/* Photo Transformation Section */}
      {childPhotoUrl && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            Your Child's Amazing Transformation!
          </h4>
          
          <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 p-4 md:p-6 rounded-xl border border-purple-100">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
              {/* Original Photo */}
              <div className="text-center flex-shrink-0">
                <p className="text-sm font-medium text-purple-700 mb-3">
                  {childName ? `${childName}'s Photo` : 'Your Photo'}
                </p>
                <div className="relative w-24 h-32 md:w-32 md:h-40 rounded-lg overflow-hidden shadow-lg border-3 border-white">
                  <img
                    src={childPhotoUrl}
                    alt={childName ? `${childName}'s photo` : 'Child\'s photo'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Transformation Arrow */}
              <div className="flex flex-col items-center flex-shrink-0">
                <span className="text-purple-600 font-medium text-xs uppercase tracking-wide mb-1">
                  becomes
                </span>
                <div className="text-purple-500">
                  <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
                <span className="text-purple-600 font-medium text-xs mt-1">
                  ✨ Hero! ✨
                </span>
              </div>

              {/* Storybook Character */}
              <div className="text-center flex-shrink-0">
                <p className="text-sm font-medium text-purple-700 mb-3">
                  Storybook Hero
                </p>
                <div className="bg-white rounded-lg shadow-lg p-1">
                  <div style={{ width: isMobile ? '96px' : '128px', height: isMobile ? '128px' : '160px' }} className="mx-auto">
                    {pdfUrl && numPages > 0 ? (
                      <Document
                        key={`cover-${pdfUrl}`}
                        file={pdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
            error={<div className="aspect-[3/4] bg-gray-100 rounded-lg" />}
                      >
                        <Page
                          pageNumber={1} // Always show cover for transformation
                          width={isMobile ? 96 : 128}
                          className="rounded shadow-sm"
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          onLoadError={(error) => {
                            console.warn('Cover PDF page load error:', error);
                          }}
                        />
                      </Document>
                    ) : (
                      <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
                        <Book className="w-6 h-6 md:w-8 md:h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-purple-700">
                <strong>{childName || 'Your child'}</strong> becomes the star of their own personalized adventure!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer - Compact and elegant */}
      <div className="bg-gray-100 rounded-lg p-2 relative">
        {pdfUrl ? (
          <Document
            key={`main-${pdfUrl}`}
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            error={<div className="aspect-[3/4] bg-gray-100 rounded-lg" />}
            className="flex justify-center"
            loading={
              <div className="aspect-[3/4] bg-gray-200 rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            }
          >
            <div className="relative group">
              {/* Auto-flip indicator */}
              {isAutoFlipping && (
                <div className="absolute top-2 right-2 z-30 bg-black/70 text-white px-2 py-1 rounded-lg text-xs font-medium backdrop-blur-sm">
                  Auto-flip
                </div>
              )}

              {/* Page counter - top left overlay */}
              {numPages > 0 && showControls && (
                <div className="absolute top-2 left-2 z-30 bg-black/70 text-white px-2 py-1 rounded-lg text-xs font-medium backdrop-blur-sm">
                  {currentPage} of {numPages}
                </div>
              )}

              {pdfError ? (
                <div className="aspect-[3/4] bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Book className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">PDF preview unavailable</p>
                  </div>
                </div>
              ) : (
                <div className={`bg-white rounded-lg shadow-lg p-0 transition-all duration-300 ${isFlipping ? 'scale-95' : 'scale-100'}`} 
                     style={{ perspective: '1000px' }}>
                  {/* Maximized page display */}
                  <div className="relative mx-auto flex justify-center">
                    <Page
                      pageNumber={currentPage}
                      width={pageWidth}
                      className="rounded shadow-sm transition-transform duration-300"
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      onLoadError={(error) => {
                        console.warn('Main PDF page load error:', error);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Overlay Navigation Buttons */}
              {numPages > 1 && !pdfError && showControls && (
                <>
                  <button
                    onClick={() => handlePageChange('prev')}
                    disabled={currentPage === 1}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 disabled:bg-black/20 disabled:cursor-not-allowed text-white rounded-full p-3 transition-all transform hover:scale-110 backdrop-blur-sm"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => handlePageChange('next')}
                    disabled={currentPage >= numPages}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 disabled:bg-black/20 disabled:cursor-not-allowed text-white rounded-full p-3 transition-all transform hover:scale-110 backdrop-blur-sm"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Auto-flip control - bottom right overlay */}
              {enableAutoFlip && !pdfError && showControls && (
                <button
                  onClick={() => setIsAutoFlipping(!isAutoFlipping)}
                  className="absolute bottom-2 right-2 z-30 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all transform hover:scale-110 backdrop-blur-sm"
                  title={isAutoFlipping ? 'Stop auto-flip' : 'Start auto-flip'}
                >
                  {isAutoFlipping ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </button>
              )}

              {/* Progress dots - bottom center overlay - only for books with 6 or fewer pages */}
              {numPages > 1 && numPages <= 6 && showControls && (
                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 flex gap-1.5 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1">
                  {Array.from({ length: numPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        currentPage === i + 1 
                          ? 'bg-white scale-125 shadow-sm' 
                          : 'bg-white/50 hover:bg-white/80 hover:scale-110'
                      }`}
                      title={`Go to page ${i + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Instructions hint */}
              {showControls && (
                <>
                  <div className="absolute top-16 right-2 text-white/70 text-xs bg-black/30 backdrop-blur-sm rounded-lg p-2 pointer-events-none hidden md:block opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="space-y-1">
                      <div>← → Arrow keys to navigate</div>
                      <div>Space for next page</div>
                      <div>Click dots to jump</div>
                    </div>
                  </div>

                  <div className="absolute top-16 right-2 text-white/70 text-xs bg-black/30 backdrop-blur-sm rounded-lg p-2 pointer-events-none md:hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="space-y-1">
                      <div>Swipe to navigate</div>
                      <div>Tap dots to jump</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Document>
        ) : (
          <div className="aspect-[3/4] bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Book className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">PDF not available</p>
            </div>
          </div>
        )}
      </div>

      {/* Remove navigation hint to avoid redundancy */}
    </div>
  );
}
